
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import type { LucideIcon } from 'lucide-react';

interface FeaturedInsightProps {
  title: string;
  description: string;
  icon: LucideIcon;
  gradientColors?: string;
  className?: string;
}

const FeaturedInsight: React.FC<FeaturedInsightProps> = ({
  title,
  description,
  icon: Icon,
  gradientColors = "from-neon-blue to-neon-purple",
  className
}) => {
  return (
    <Card 
      className={cn(
        "backdrop-blur-md hover:-translate-y-1 transition-all duration-300 cursor-pointer group overflow-hidden border-white/10 bg-black/50",
        className
      )}
      variant="neo"
    >
      {/* Background gradient effect */}
      <div className={cn(
        "absolute inset-0 opacity-10 bg-gradient-to-br",
        gradientColors
      )} />
      
      {/* Animated highlight line */}
      <div className="absolute top-0 left-0 w-full h-[1px] overflow-hidden">
        <div className={cn(
          "h-full w-1/2 animate-[move-bg-insight_2s_ease-in-out_infinite_alternate] bg-gradient-to-r",
          gradientColors
        )} />
      </div>
      
      <CardContent className="p-4 relative">
        <div className="flex gap-4">
          <div className={cn(
            "h-12 w-12 shrink-0 rounded-full flex items-center justify-center bg-gradient-to-br shadow-lg relative group-hover:scale-110 transition-transform duration-500",
            gradientColors
          )}>
            <Icon className="h-5 w-5 text-white" />
            
            {/* Animated ring around the icon */}
            <div className="absolute -inset-1 rounded-full border border-white/20 opacity-0 group-hover:opacity-100 animate-pulse-soft transition-opacity" />
          </div>
          
          <div className="space-y-1.5">
            <h3 className="font-medium text-white group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r transition-colors duration-500"
              style={{
                backgroundImage: `linear-gradient(to right, var(--tw-gradient-stops))`,
              }}
            >{title}</h3>
            <p className="text-sm text-white/70">{description}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default FeaturedInsight;
